import { Backdrop, Box, CircularProgress, Menu, MenuItem } from '@mui/material';
import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import Dialog from '@mui/material/Dialog';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import TimelineIcon from '@mui/icons-material/Timeline';
import TranslateIcon from '@mui/icons-material/Translate';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { ListItemIcon, ListItemText } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import fileSystemAccessServiceInstance from '../../services/FileSystemAccessService';
import videoCRUDServiceInstance from '../../services/VideoCRUDService';
import videoUploadService from '../../services/VideoUploadService';
import MoveDialog from '../dialogs/MoveDialog';
import ProgressStagesDialog from '../dialogs/ProgressStagesDialog';
import Text2TextDialog from '../dialogs/Text2TextDialog';
import VideoHistoryDialog from '../dialogs/VideoHistoryDialog';
import ExperienceSwitcher from '../ExperienceSwitcher';
import ModernFolderView from '../navigation/ModernFolderView';
import { getLanguagesForCreateVideo } from '../subtitles/SelectLanguageComponent';
import UploadVideoDialog from '../subtitles/UploadVideoDialog';
import { LinkTypes } from '../../utils/constants';
import { useAssignmentContext } from '../../contexts/AssignmentContext';
import UserService from '../../services/UserService';
import AssignmentService from '../../services/AssignmentService';
import {DialogTitle, DialogContent, DialogActions} from '@mui/material';
import { Alert, Button, Chip, FormControl, InputLabel, Select, TextField } from '@mui/material';
import { DialogContentText, Typography } from '@mui/material';

const AssignVideoDialog = ({ open, onClose, videoInfo }) => {
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Level 1'); // Default to Level 1
  const [dueDate, setDueDate] = useState(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [existingAssignments, setExistingAssignments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const { assignVideo } = useAssignmentContext();

  const categories = ['Level 1', 'Level 2', 'Level 3'];

  useEffect(() => {
    //console.log("AssignVideoDialog mounted", { open, videoInfo });
    if (open && videoInfo) {
      fetchUsers();
      fetchExistingAssignments();
    }
  }, [open, videoInfo]);

  const fetchUsers = async () => {
    const authUser = JSON.parse(localStorage.getItem('authUser'));
    const authToken = JSON.parse(localStorage.getItem('authToken'));

    try {
      const response = await UserService.getAllUsers(authUser.email, authToken);
      if (response) {
        setUsers(response);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Failed to fetch users");
    }
  };

  const fetchExistingAssignments = async () => {
    if (!videoInfo || !videoInfo.id) return;

    try {
      setLoading(true);
      const authUser = JSON.parse(localStorage.getItem('authUser'));
      const authToken = JSON.parse(localStorage.getItem('authToken'));

      const response = await AssignmentService.getAssignmentsForVideo(
        videoInfo.id,
        authUser.email,
        authToken
      );

      if (response && response.data) {
        setExistingAssignments(response.data);
      }
    } catch (error) {
      console.error("Error fetching existing assignments:", error);
      setError("Failed to fetch existing assignments");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedUser || !selectedLanguage || !dueDate || !selectedCategory) {
      setError("Please fill all required fields");
      return;
    }

    // Check if this exact language-category combination is already assigned
    const isDuplicate = existingAssignments.some(
      assignment => 
        assignment.languageName === selectedLanguage && 
        assignment.category === selectedCategory
    );

    if (isDuplicate) {
      setError(`${selectedLanguage} is already assigned with category ${selectedCategory}`);
      return;
    }

    try {
      setLoading(true);

      // Ensure the time is set to 9:00 PM
      const dueDateWithTime = new Date(dueDate);
      dueDateWithTime.setHours(21, 0, 0, 0); // Set to 9:00:00 PM

      const assignment = {
        videoId: videoInfo.id,
        videoName: videoInfo.videoName || videoInfo.name,
        languageName: selectedLanguage,
        assignedTo: selectedUser,
        dueDate: dueDateWithTime.toISOString(),
        notes: notes,
        folderPath: videoInfo.path || '',
        category: selectedCategory
      };

      await assignVideo(assignment);

      setSuccess(`Successfully assigned ${selectedLanguage} to ${selectedUser} (${selectedCategory})`);
      setSelectedLanguage('');
      fetchExistingAssignments();

      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (error) {
      console.error("Error creating assignment:", error);
      setError("Failed to create assignment: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getAvailableLanguages = () => {
    if (!videoInfo || !videoInfo.videoLanguages) return [];

    // Instead of filtering out all assigned languages, filter based on language+category combination
    const assignedCombinations = existingAssignments.map(a => `${a.languageName}-${a.category}`);
    
    // Create a list of all possible language/category combinations
    const allLanguages = [];
    
    videoInfo.videoLanguages.forEach(lang => {
      // Include each language, even if it's already assigned with a different category
      if (!allLanguages.some(l => l.languageName === lang.languageName)) {
        allLanguages.push(lang);
      }
    });
    
    return allLanguages;
  };

  const availableLanguages = getAvailableLanguages();

  const handleDeleteAssignment = async (assignmentId) => {
    try {
      setLoading(true);
      const authUser = JSON.parse(localStorage.getItem('authUser'));
      const authToken = JSON.parse(localStorage.getItem('authToken'));

      await AssignmentService.deleteAssignment(
        assignmentId,
        authUser.email,
        authToken
      );

      setSuccess("Assignment deleted successfully");
      fetchExistingAssignments();
    } catch (error) {
      console.error("Error deleting assignment:", error);
      setError("Failed to delete assignment");
    } finally {
      setLoading(false);
    }
  };

  const renderExistingAssignments = () => {
    if (existingAssignments.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No assignments yet for this video.
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        {existingAssignments.map((assignment) => (
          <Chip
            key={assignment.id}
            label={`${assignment.languageName} → ${assignment.assignedTo} (${assignment.category || 'Unspecified'})`}
            color={assignment.status === 'COMPLETED' ? 'success' : getCategoryColor(assignment.category)}
            onDelete={() => handleDeleteAssignment(assignment.id)}
            sx={{ my: 0.5 }}
          />
        ))}
      </Box>
    );
  };

  const getCategoryColor = (category) => {
    switch(category) {
      case 'Level 1': return 'info';
      case 'Level 2': return 'secondary';
      case 'Level 3': return 'primary';
      default: return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <AssignmentIcon sx={{ mr: 1 }} />
        Assign Video for Translation
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <DialogContentText>
          Assign video "{videoInfo?.videoName || videoInfo?.name}" for translation or editing.
        </DialogContentText>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" fontWeight={500} gutterBottom>
            Current Assignments:
          </Typography>

          {renderExistingAssignments()}

          <Typography variant="subtitle1" fontWeight={500} gutterBottom>
            Create New Assignment:
          </Typography>

          <FormControl fullWidth margin="normal">
            <InputLabel id="language-label">Language</InputLabel>
            <Select
              labelId="language-label"
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              label="Language"
            >
              {availableLanguages.length === 0 && (
                <MenuItem disabled value="">
                  No unassigned languages available
                </MenuItem>
              )}
              {availableLanguages.map((lang) => (
                <MenuItem key={lang.languageName} value={lang.languageName}>
                  {lang.languageName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel id="user-label">Assign To</InputLabel>
            <Select
              labelId="user-label"
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              label="Assign To"
            >
              {users.map((user) => (
                <MenuItem key={user.email} value={user.email}>
                  {user.name || user.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel id="category-label">Category</InputLabel>
            <Select
              labelId="category-label"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              label="Category"
            >
              {categories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Due Date"
            type="date" // Change to date-only picker to avoid time confusion
            value={dueDate ? new Date(dueDate).toISOString().split('T')[0] : ''}
            onChange={(e) => {
              // Always set time to 9:00 PM when any date is selected
              const selectedDate = new Date(e.target.value);
              selectedDate.setHours(21, 0, 0, 0); // Set to 9:00:00 PM
              setDueDate(selectedDate);
            }}
            fullWidth
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              min: new Date().toISOString().split('T')[0]
            }}
            helperText="Due date will be set to 9:00 PM on the selected day"
          />

          <TextField
            label="Notes (optional)"
            multiline
            rows={3}
            fullWidth
            margin="normal"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="error" >Cancel</Button>
        <Button 
          onClick={() => {
            setSuccess('Assignment management completed');
            setTimeout(() => {
              onClose();
            }, 1000);
          }}
          color="secondary"
        >
          Done
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="outlined" 
          color ="primary"
          disabled={!selectedUser || !selectedLanguage || !dueDate || !selectedCategory || loading}
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignVideoDialog;